/**
 * @file SystemDesignPage.jsx
 * @description Interactive reader for the System Design curriculum.
 *              Features a 3-column layout: collapsible topic sidebar, Notion-rendered
 *              article pane, and a sticky table-of-contents sidebar with scroll-spy.
 *              Supports regular chapter content (public) and interview designs (auth-gated).
 *              Includes mobile drawer navigation and progress tracking (mark-as-complete).
 *
 * @module components/SystemDesignPage
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, publicApiGet } from '../utils/apiUtils';
import {
    FiCode,
    FiMenu,
    FiX,
    FiChevronRight,
    FiCheckCircle,
    FiBookOpen,
    FiLayers,
    FiRefreshCw,
    FiAlertCircle,
    FiExternalLink,
    FiCheck
} from 'react-icons/fi';
import { FaCircle } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import NotionRenderer from './NotionRenderer';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component SystemDesignPage
 * @description The primary System Design reading interface.
 *              Fetches topic trees, renders Notion content, tracks user progress,
 *              and provides desktop/mobile navigation with scroll-spy TOC.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 *
 * @returns {JSX.Element} The fully assembled System Design reader view.
 */
const SystemDesignPage = ({ navigate }) => {
    const { isAuthenticated, user, signInWithGoogle } = useAuth();

    // Set browser tab title on mount
    useEffect(() => { document.title = 'SimpleCode | System Design'; }, []);

    // State: UI controls
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [expandedTopics, setExpandedTopics] = useState({});
    const [activeHeading, setActiveHeading] = useState(null);

    // State: Content data
    const [topics, setTopics] = useState([]);
    const [selectedContent, setSelectedContent] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [notionData, setNotionData] = useState(null);
    const [userProgress, setUserProgress] = useState({});

    // State: Loading and error
    const [loading, setLoading] = useState(true);
    const [contentLoading, setContentLoading] = useState(false);
    const [error, setError] = useState(null);
    const [markingComplete, setMarkingComplete] = useState(false);

    // Refs: Scroll-spy debounce control
    const isClickScrolling = useRef(false);
    const scrollTimeout = useRef(null);

    /* ── UI Toggle Handlers ── */

    /** Opens/closes the mobile sidebar drawer. */
    const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);
    /** Explicitly closes the mobile sidebar (used after content selection). */
    const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

    /** Toggles a sidebar topic section between expanded/collapsed states. */
    const toggleTopic = (topicId) => {
        setExpandedTopics(prev => ({
            ...prev,
            [topicId]: !prev[topicId]
        }));
    };

    /**
     * Smooth-scrolls to a heading element in the article pane.
     * Temporarily disables the scroll-spy observer to prevent fighting.
     */
    const scrollToHeading = (headingId) => {
        const element = document.getElementById(`block-${headingId}`);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            isClickScrolling.current = true;
            setActiveHeading(headingId);

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            // Re-enable scroll-spy after the smooth scroll completes
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
            scrollTimeout.current = setTimeout(() => {
                isClickScrolling.current = false;
            }, 800);
        }
    };

    /* ── Data Fetching ── */

    /** Fetches the full topic tree and auto-selects initial content (via URL param or first item). */
    const fetchTopics = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await publicApiGet('/system-design/topics');

            if (response.ok) {
                const data = await response.json();
                setTopics(data);

                const params = new URLSearchParams(window.location.search);
                const contentIdParam = params.get('contentId');
                
                let targetTopic = null;
                let targetContent = null;

                if (contentIdParam) {
                    for (const topic of data) {
                        const content = topic.content?.find(c => c.id == contentIdParam);
                        if (content) {
                            targetContent = content;
                            targetTopic = topic;
                            break;
                        }
                    }
                }

                if (!targetContent && data && data.length > 0 && data[0].content && data[0].content.length > 0) {
                    targetTopic = data[0];
                    targetContent = data[0].content[0];
                }

                if (targetTopic && targetContent) {
                    setExpandedTopics(prev => ({ ...prev, [targetTopic.id]: true }));
                    handleContentSelect(targetContent, targetTopic, true);
                }
            } else {
                throw new Error('Failed to fetch topics');
            }
        } catch (err) {
            setError('Failed to load system design content');
        } finally {
            setLoading(false);
        }
    };

    /** Fetches the user's completion map for all content items. */
    const fetchUserProgress = async () => {
        if (!isAuthenticated || !user?.id) return;

        try {
            const response = await apiGet(`/system-design/progress/${user.id}`);
            if (response.ok) {
                const data = await response.json();
                setUserProgress(data);
            }
        } catch (err) {
        }
    };

    /** Fetches Notion-rendered blocks for a specific content item. Auth-gates interview content. */
    const fetchNotionContent = async (content, topicType) => {
        try {
            setContentLoading(true);

            const apiCall = topicType === 'interview' && isAuthenticated ? apiGet : publicApiGet;
            const response = await apiCall(`/system-design/notion/${content.notion_page_id}`);

            if (response.ok) {
                const data = await response.json();
                setNotionData(data);
            } else if (response.status === 401) {
                const errorData = await response.json();
                setError(errorData.message || 'Please sign in to view interview questions');
                setTimeout(() => {
                    navigate('/signin');
                }, 2000);
            } else {
                throw new Error('Failed to fetch content');
            }
        } catch (err) {
            setError('Failed to load chapter content');
        } finally {
            setContentLoading(false);
        }
    };

    /** Handles sidebar content selection: updates state, fetches Notion data, and manages URL params. */
    const handleContentSelect = async (content, topic, skipUrlUpdate = false) => {
        setSelectedContent(content);
        setSelectedTopic(topic);
        setNotionData(null);
        setActiveHeading(null);

        if (!skipUrlUpdate) {
            const url = new URL(window.location);
            url.searchParams.set('contentId', content.id);
            window.history.replaceState({}, '', url);
        }

        if (topic.type === 'regular') {
            await fetchNotionContent(content, topic.type);
        }

        if (window.innerWidth < 1024) {
            closeMobileSidebar();
        }

        if (!skipUrlUpdate) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    /** Opens the full interview design view in a new browser tab. */
    const openInNewTab = (content) => {
        const url = `/system-design/view/${content.id}`;
        window.open(url, '_blank');
    };

    /** Marks the currently selected content item as completed and updates local progress state. */
    const markAsComplete = async () => {
        if (!isAuthenticated || !user?.id) {
            alert('Please sign in to track your progress');
            return;
        }

        try {
            setMarkingComplete(true);
            const response = await apiPost('/system-design/complete', {
                contentId: parseInt(selectedContent.id)
            });

            if (response.ok) {
                setUserProgress(prev => ({
                    ...prev,
                    [selectedContent.id]: {
                        completed: true,
                        completed_at: new Date().toISOString()
                    }
                }));
            } else {
                throw new Error('Failed to mark as complete');
            }
        } catch (err) {
            alert('Failed to save progress');
        } finally {
            setMarkingComplete(false);
        }
    };

    /* ── Effects ── */

    // Scroll-spy: tracks which heading is currently in view and highlights the TOC entry
    useEffect(() => {
        if (!notionData?.headings || selectedTopic?.type !== 'regular') return;

        const handleScroll = () => {
            if (isClickScrolling.current) return;

            const scrollPosition = window.scrollY + 200;

            for (let i = notionData.headings.length - 1; i >= 0; i--) {
                const heading = notionData.headings[i];
                const element = document.getElementById(`block-${heading.id}`);

                if (element && element.offsetTop <= scrollPosition) {
                    setActiveHeading(heading.id);

                    setTimeout(() => {
                        const navButton = document.getElementById(`nav-${heading.id}`);
                        if (navButton) {
                            const navContainer = navButton.closest('.overflow-y-auto');
                            if (navContainer) {
                                const containerRect = navContainer.getBoundingClientRect();
                                const buttonRect = navButton.getBoundingClientRect();

                                if (buttonRect.top < containerRect.top || buttonRect.bottom > containerRect.bottom) {
                                    navButton.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center'
                                    });
                                }
                            }
                        }
                    }, 100);

                    break;
                }
            }
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [notionData, selectedTopic]);

    // Primary data fetch on mount / auth change
    useEffect(() => {
        fetchTopics();
        fetchUserProgress();
    }, [isAuthenticated, user]);

    if (loading) {
        return <SystemDesignSkeleton navigate={navigate} />;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
                <Navbar navigate={navigate} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md mx-4 bg-white border-l-4 border-red-500 p-8 rounded">
                        <FiAlertCircle className="text-6xl text-red-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold mb-2 text-foreground">Error Loading Content</h2>
                        <p className="text-muted-foreground mb-6">{error}</p>
                        <button
                            onClick={fetchTopics}
                            className="px-6 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded font-semibold transition-all duration-200"
                        >
                            <FiRefreshCw className="inline mr-2" />
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const isCompleted = selectedContent ? userProgress[selectedContent.id]?.completed : false;

    // Standardized nav links for Navbar & Footer
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") }
    ];
    if (isAuthenticated) navLinks.splice(2, 0, { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard") });

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "Learning", action: () => navigate("/learning") },
        ...(isAuthenticated ? [{ label: "Dashboard", action: () => navigate("/dashboard") }] : []),
        { label: "DSA Problems", action: () => navigate("/dsa-problems") }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 py-16 scroll-smooth" style={{ scrollPaddingTop: '80px' }}>
                <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-2 xl:gap-2">

                        {/* ── LEFT SIDEBAR: Navigation Floating on Background ── */}
                        <aside className="hidden lg:block lg:col-span-3 xl:col-span-3">
                            <div style={{ position: 'sticky', top: '100px', width: '100%', maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
                                <div className="pb-4 mb-4" style={{ flexShrink: 0 }}>
                                    <h1 className="text-xl lg:text-4xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8 flex items-center">
                                        System <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Design</span>
                                    </h1>
                                </div>

                                <div style={{ flex: 1, overflowY: 'auto' }} className="pr-3 space-y-1">
                                    {topics.map((topic) => (
                                        <div key={topic.id} className="mb-2">
                                            <button onClick={() => toggleTopic(topic.id)}
                                                className={`font-semibold w-full text-left py-2 transition-all duration-200 flex items-center justify-between rounded ${selectedTopic?.id === topic.id
                                                    ? 'text-emerald-700'
                                                    : 'text-foreground hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}>
                                                <div className="flex items-center flex-1">
                                                    {/* {topic.type === 'interview' ? <FiCode className="w-4 h-4 text-emerald-600" /> : <FiBookOpen className="w-4 h-4 text-emerald-600" />} */}
                                                    <span className="text-md">{topic.title}</span>
                                                </div>
                                                <div style={{ transform: `rotate(${expandedTopics[topic.id] ? 90 : 0}deg)`, transition: 'transform 0.2s' }}>
                                                    <FiChevronRight className={`w-4 h-4 font-semibold ${selectedTopic?.id === topic.id ? 'text-emerald-700' : 'text-muted-foreground'}`} />
                                                </div>
                                            </button>
                                            
                                            {/* Sub-items list */}
                                            <div className={`grid transition-all duration-300 ease-in-out ${expandedTopics[topic.id] ? 'grid-rows-[1fr] opacity-100 mt-1' : 'grid-rows-[0fr] opacity-0'}`}>
                                                <div className="overflow-hidden">
                                                    <div className="border-l-2 border-emerald-600 flex flex-col space-y-0.5 pl-2 py-0.5">
                                                        {topic.content.map((content) => {
                                                        const isCompleted = userProgress[content.id]?.completed;
                                                        const isActive = selectedContent?.id === content.id;
                                                        return (
                                                            <button key={content.id} onClick={() => handleContentSelect(content, topic)} 
                                                                className={`w-full text-left px-2 py-1 transition-all duration-200 flex items-start rounded text-sm font-semibold ${isActive ? 'text-emerald-700 bg-white' : 'text-muted-foreground hover:text-foreground'}`}>
                                                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                                                    <div className="flex-shrink-0 mt-0.5">
                                                                        {isCompleted ? (<FiCheck className="text-emerald-700 w-3.5 h-3.5" />) : (<FaCircle className="text-gray-600 w-2 h-2" />)}
                                                                    </div>
                                                                    <span className="flex-1 truncate">{content.title}</span>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </aside>

                        {/* ── MOBILE NAV BAR ── */}
                        <div className="lg:hidden mb-4">
                            <button onClick={toggleMobileSidebar} className="flex items-center space-x-2 px-4 py-3 bg-white  border-l-4 border-emerald-600 rounded text-foreground hover:text-emerald-600 transition-all w-full">
                                <FiMenu className="w-4 h-4" />
                                <span className="text-sm font-semibold flex-1 text-left truncate">{selectedContent ? selectedContent.title : 'Select a Topic'}</span>
                                <FiChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* ── MOBILE SIDEBAR OVERLAY ── */}
                        <>
                            {/* Backdrop */}
                            <div 
                                className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${isMobileSidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`} 
                                onClick={closeMobileSidebar} 
                            />
                            
                            {/* Drawer */}
                            <div 
                                className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-emerald-900 shadow-2xl z-50 lg:hidden overflow-y-auto transform transition-transform duration-300 ease-in-out ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
                            >
                                <div className="py-4 px-5 border-b border-border flex items-center justify-between">
                                    <h2 className="text-xl font-bold text-white flex items-center">
                                        System Design
                                    </h2>
                                    <button onClick={closeMobileSidebar} className="text-gray-300 hover:text-white transition-colors">
                                        <FiX className="w-6 h-6" />
                                    </button>
                                </div>
                                
                                <div className="p-4 space-y-2">
                                    {topics.map((topic) => (
                                        <div key={topic.id} className="mb-2">
                                            <button onClick={() => toggleTopic(topic.id)} 
                                                className={`font-semibold w-full text-left px-2 py-2 transition-all duration-200 flex items-center justify-between rounded ${selectedTopic?.id === topic.id ? 'text-white' : 'text-gray-300 hover:text-white'}`}>
                                                <div className="flex items-center flex-1">
                                                    <span className="text-md">{topic.title}</span>
                                                </div>
                                                <div style={{ transform: `rotate(${expandedTopics[topic.id] ? 90 : 0}deg)`, transition: 'transform 0.2s' }}>
                                                    <FiChevronRight className={`w-4 h-4 font-semibold ${selectedTopic?.id === topic.id ? 'text-white' : 'text-gray-300'}`} />
                                                </div>
                                            </button>
                                            
                                            {/* Sub-items list */}
                                            <div className={`grid transition-all duration-300 ease-in-out ${expandedTopics[topic.id] ? 'grid-rows-[1fr] opacity-100 mt-1' : 'grid-rows-[0fr] opacity-0'}`}>
                                                <div className="overflow-hidden">
                                                    <div className="ml-2 pl-2 border-l-2 border-emerald-700 flex flex-col space-y-0.5 py-0.5">
                                                        {topic.content.map((content) => {
                                                            const isCompleted = userProgress[content.id]?.completed;
                                                            const isActive = selectedContent?.id === content.id;
                                                            return (
                                                                <button key={content.id} onClick={() => handleContentSelect(content, topic)} 
                                                                    className={`w-full text-left px-2 py-1.5 transition-all duration-200 flex items-start rounded text-sm font-semibold ${isActive ? 'text-emerald-700 bg-white' : 'text-gray-400 hover:text-gray-200'}`}>
                                                                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                                                                        <div className="flex-shrink-0">
                                                                            {isCompleted ? (<FiCheck className={`w-3.5 h-3.5 ${isActive ? 'text-emerald-700 bg-white' : 'text-gray-400 hover:text-gray-200'}`} />) : (<FaCircle className="text-gray-400 w-2 h-2" />)}
                                                                        </div>
                                                                        <span className="flex-1 truncate">{content.title}</span>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>

                        {/* ── MIDDLE PANE: Main Content Document Paper ── */}
                        {!selectedContent ? (
                            <div className="lg:col-span-9 xl:col-span-9 flex items-center justify-center">
                                <div className="bg-white border-l-4 border-emerald-600 rounded  p-12 text-center w-full max-w-2xl">
                                    <FiBookOpen className="text-6xl text-emerald-100 mx-auto mb-6" />
                                    <h3 className="text-2xl font-bold text-foreground mb-3">Select a Topic</h3>
                                    <p className="text-muted-foreground">Choose a chapter or interview question from the sidebar to start reading.</p>
                                </div>
                            </div>
                        ) : selectedTopic?.type === 'interview' ? (
                            <article className="lg:col-span-9 xl:col-span-9">
                                <div className="bg-white rounded  border-l-4 border-emerald-600 relative overflow-hidden mb-2">
                                    <div className="p-4 md:p-6 lg:p-8 mt-1">
                                        <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-8 text-left leading-tight">{selectedContent.title}</h2>
                                        {selectedContent.introduction && (
                                            <p className="text-slate-700 mb-8 mx-1 leading-relaxed text-justify text-md whitespace-pre-wrap">{selectedContent.introduction}</p>
                                        )}
                                        <div className="flex justify-start pt-6 border-t border-gray-200">
                                            {isAuthenticated ? (
                                                <button onClick={() => openInNewTab(selectedContent)} className="inline-flex items-center justify-start text-left space-x-2 text-muted-foreground hover:text-emerald-700 font-semibold rounded transition-all">
                                                    <span>See Complete Design</span>
                                                    <FiExternalLink className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <button onClick={signInWithGoogle} className="inline-flex items-center space-x-2 text-muted-foreground hover:text-emerald-700 font-semibold rounded transition-all">
                                                    <FcGoogle className="w-4 h-4" />
                                                    <span>Sign in with Google to view</span>
                                                    <FiExternalLink className="w-4 h-4 ml-1 opacity-70" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ) : (
                            <>
                                <article className="lg:col-span-7 xl:col-span-7">
                                    <div className="bg-white rounded  min-h-[85vh] border-l-4 border-emerald-600 relative overflow-hidden">
                                        {/* <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 to-teal-400 absolute top-0 left-0"></div> */}
                                        <div className="p-4 md:p-6 lg:p-6 mt-1">
                                            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-8 text-left leading-tight">{selectedContent.title}</h2>
                                            {contentLoading ? (
                                                <ContentLoadingSkeleton />
                                            ) : notionData?.blocks ? (
                                                <div className="prose prose-emerald prose-lg max-w-none prose-headings:text-foreground prose-p:text-slate-600 prose-a:text-emerald-600 hover:prose-a:text-emerald-700">
                                                    <NotionRenderer blocks={notionData.blocks} />
                                                    
                                                    {/* Completion Actions inside the document */}
                                                    <div className="mt-8 pt-8 border-t border-gray-200 flex justify-center">
                                                        {isAuthenticated && !isCompleted && (
                                                            <button
                                                                onClick={markAsComplete}
                                                                disabled={markingComplete}
                                                                className="px-2 py-1 text-muted-foreground hover:text-emerald-700 font-semibold rounded transition-all  flex items-center justify-center space-x-2 min-w-[200px]"
                                                            >
                                                                {markingComplete ? (
                                                                    <>
                                                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                        <span>Saving...</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span>Mark as Completed</span>
                                                                        <FiCheck className="w-5 h-5" />
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                        {isCompleted && (
                                                            <div className="inline-flex items-center space-x-3 text-emerald-700">
                                                                <span className="font-semibold text-lg">You've completed this chapter!</span>
                                                                {/* <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white"> */}
                                                                    <FiCheck className="text-xl font-bold text-emerald-700" />
                                                                {/* </div> */}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-16">
                                                    <FiAlertCircle className="text-4xl text-red-400 mx-auto mb-4" />
                                                    <p className="text-muted-foreground">Failed to load content.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </article>

                                {/* ── RIGHT SIDEBAR: Table of Contents Floating on Background ── */}
                                <aside className="hidden lg:block lg:col-span-2 xl:col-span-2">
                                    <div style={{ position: 'sticky', top: '100px', width: '100%', maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
                                        <div className="py-2 pl-1 mb-2" style={{ flexShrink: 0 }}>
                                            <h3 className="text-sm font-bold text-foreground tracking-wider uppercase underline">On This Page</h3>
                                        </div>
                                        <div style={{ flex: 1, overflowY: 'auto' }} className="py-2 pl-2">
                                            {notionData?.headings && notionData.headings.length > 0 ? (
                                                <div className="space-y-1 relative border-l-2 border-emerald-200 ml-1">
                                                    {/* Smooth sliding border indicator */}
                                                    <div 
                                                        className="absolute -left-[2px] w-[2px] bg-emerald-600 transition-all duration-300 ease-out z-10"
                                                        style={{ 
                                                            top: activeHeading ? `${Math.max(0, notionData.headings.findIndex(h => h.id === activeHeading)) * 36}px` : '0px',
                                                            height: '32px',
                                                            opacity: activeHeading && notionData.headings.findIndex(h => h.id === activeHeading) !== -1 ? 1 : 0
                                                        }}
                                                    />
                                                    {notionData.headings.map((heading) => (
                                                        <button
                                                            key={heading.id}
                                                            id={`nav-${heading.id}`}
                                                            onClick={() => scrollToHeading(heading.id)}
                                                            className={`w-full text-left truncate py-1.5 text-sm transition-all duration-200 relative -ml-[2px] border-l-2 border-transparent ${activeHeading === heading.id ? 'text-emerald-700 font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                                                            style={{ paddingLeft: `${(heading.level - 1) * 8 + 12}px` }}
                                                            title={heading.text}
                                                        >
                                                            {heading.text}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-muted-foreground text-xs px-2 py-4 italic">No sub-headings</p>
                                            )}
                                        </div>
                                    </div>
                                </aside>
                            </>
                        )}
                    </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADERS
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component SystemDesignSkeleton
 * @description Full-page loading placeholder that mimics the 3-column layout.
 */
const SystemDesignSkeleton = ({ navigate }) => {
    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} />
            <main className="flex-1 py-16">
                <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 xl:gap-8">
                        {/* Navigation Skeleton */}
                        <aside className="hidden lg:block lg:col-span-3 xl:col-span-3 animate-pulse">
                            <div className="space-y-4">
                                <div className="h-8 w-3/4 bg-slate-200 rounded mb-6"></div>
                                <div className="space-y-3">
                                    <div className="h-10 w-full bg-slate-200 rounded"></div>
                                    <div className="h-10 w-full bg-slate-200 rounded"></div>
                                    <div className="h-10 w-full bg-slate-200 rounded"></div>
                                    <div className="h-10 w-full bg-slate-200 rounded"></div>
                                </div>
                            </div>
                        </aside>

                        {/* Content Skeleton */}
                        <div className="lg:col-span-7 xl:col-span-7 animate-pulse">
                            <div className="bg-white rounded-xl  min-h-[85vh] border border-border p-8 lg:p-12">
                                <div className="h-10 w-2/3 bg-slate-200 rounded mb-8"></div>
                                <div className="space-y-3 mb-8">
                                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                                    <div className="h-4 w-5/6 bg-slate-200 rounded"></div>
                                </div>
                                <div className="h-64 w-full bg-slate-200 rounded mb-8"></div>
                                <div className="space-y-3">
                                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                                    <div className="h-4 w-3/4 bg-slate-200 rounded"></div>
                                </div>
                            </div>
                        </div>

                        {/* TOC Skeleton */}
                        <aside className="hidden lg:block lg:col-span-2 xl:col-span-2 animate-pulse">
                            <div className="space-y-2 p-4">
                                <div className="h-4 w-1/2 bg-slate-200 rounded mb-4"></div>
                                <div className="space-y-3">
                                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                                    <div className="h-4 w-5/6 bg-slate-200 rounded"></div>
                                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                                    <div className="h-4 w-4/5 bg-slate-200 rounded"></div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </main>
            <Footer navigate={navigate} />
        </div>
    );
};

/**
 * @component ContentLoadingSkeleton
 * @description Inline loading placeholder for the article pane while Notion content fetches.
 */
const ContentLoadingSkeleton = () => {
    return (
        <div className="animate-pulse space-y-6">
            <div className="space-y-3">
                <div className="h-4 w-full bg-slate-200 rounded"></div>
                <div className="h-4 w-full bg-slate-200 rounded"></div>
                <div className="h-4 w-5/6 bg-slate-200 rounded"></div>
            </div>
            <div className="h-64 w-full bg-slate-100 rounded"></div>
            <div className="space-y-3">
                <div className="h-4 w-full bg-slate-200 rounded"></div>
                <div className="h-4 w-4/5 bg-slate-200 rounded"></div>
                <div className="h-4 w-full bg-slate-200 rounded"></div>
            </div>
            <div className="h-32 w-full bg-slate-100 rounded"></div>
            <div className="space-y-3">
                <div className="h-4 w-full bg-slate-200 rounded"></div>
                <div className="h-4 w-3/4 bg-slate-200 rounded"></div>
            </div>
        </div>
    );
};

export default SystemDesignPage;